/**
 * Trip Statement (downloadable PDF) — data derivation.
 *
 * Given a `trip` (array of bookings from FlightBooking entity), plus passengers
 * and the current user, produce a plain-object snapshot suitable for rendering
 * into a print-ready HTML document. Kept as a pure module (no React, no fetch)
 * so it can be unit-tested and reused.
 *
 * Confidentiality contract:
 *   The airline confirmation number is included ONLY when the trip is marked
 *   `completed`. Before that, the customer's ticket has not been issued and we
 *   must not disclose a PNR that the airline hasn't finalized (matches how
 *   FlightDetailsModal gates the same field in the modal header).
 */

import { getCityName } from '@/components/airportUtils';
import { CONTACT_PAGE_DISPLAY, SUPPORT_SMS_DISPLAY } from '@/constants/support';
import { getServiceFeeRate } from '@/utils';
import { checkoutSeatTotalFromFlightData, checkoutSeatCountFromFlightData } from '@/utils/seatTotalFromFlightData';
import { insurancePremiumCents, readTripInsurance, withoutInsurancePremium } from '../../supabase/functions/_shared/trip-insurance.js';
import { resolveAirlineIata, resolveCarrierLogo } from '@/utils/airlineBranding';

const MONEY = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

export function formatMoney(value, currency = 'USD') {
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  if (currency && currency !== 'USD') {
    try {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(n);
    } catch {
      /* fall through to USD */
    }
  }
  return MONEY.format(n);
}

export function formatMoneyCents(cents, currency = 'USD') {
  const n = Number(cents);
  if (!Number.isFinite(n)) return '—';
  return formatMoney(n / 100, currency);
}

/**
 * Return the calendar YYYY-MM-DD portion regardless of whether the input is
 * an ISO datetime, a space-separated timestamp, or already a bare date.
 */
export function calendarDateOnly(value) {
  if (!value) return '';
  const s = String(value);
  if (s.length >= 10) return s.slice(0, 10);
  return '';
}

/** Human date, e.g. "Sat, Jan 18, 2026". Never throws — always returns a string. */
export function formatLongDate(value) {
  const ymd = calendarDateOnly(value);
  if (!ymd) return '';
  const [y, m, d] = ymd.split('-').map((n) => parseInt(n, 10));
  if (!y || !m || !d) return '';
  const dt = new Date(Date.UTC(y, m - 1, d));
  return dt.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

/** Short date, e.g. "Jan 18, 2026". */
export function formatShortDate(value) {
  const ymd = calendarDateOnly(value);
  if (!ymd) return '';
  const [y, m, d] = ymd.split('-').map((n) => parseInt(n, 10));
  if (!y || !m || !d) return '';
  const dt = new Date(Date.UTC(y, m - 1, d));
  return dt.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

/** "10:35 AM" style formatter — accepts either "HH:MM" or full ISO datetimes. */
export function formatTime12(value) {
  if (!value) return '';
  const s = String(value);
  let hh; let mm;
  if (s.includes('T')) {
    const t = s.split('T')[1] || '';
    [hh, mm] = t.split(':');
  } else if (s.includes(' ')) {
    const t = s.split(' ')[1] || '';
    [hh, mm] = t.split(':');
  } else if (s.includes(':')) {
    [hh, mm] = s.split(':');
  }
  const h = parseInt(hh || '', 10);
  const m = parseInt(mm || '', 10);
  if (Number.isNaN(h) || Number.isNaN(m)) return '';
  const ampm = h >= 12 ? 'PM' : 'AM';
  const h12 = h % 12 || 12;
  const mmStr = String(m).padStart(2, '0');
  return `${h12}:${mmStr} ${ampm}`;
}

/**
 * Normalize a passenger from any of: FlightBooking-joined passenger row,
 * the Passenger entity's `Passenger` shape, or the customer's on-cart edit
 * shape used by FlightDetailsModal.
 */
export function normalizePassenger(raw = {}) {
  const first = raw.firstName || raw.first_name || raw.givenName || raw.given_name || '';
  const middle = raw.middleName || raw.middle_name || '';
  const last = raw.lastName || raw.last_name || raw.familyName || raw.family_name || '';
  return {
    id: raw.id || null,
    title: raw.title || '',
    firstName: first,
    middleName: middle,
    lastName: last,
    fullName: [first, middle, last].filter(Boolean).join(' ').trim(),
    type: raw.type || 'adult',
    gender: raw.gender || '',
    dateOfBirth: raw.bornOn || raw.born_on || raw.dateOfBirth || raw.date_of_birth || '',
    email: raw.email || '',
    phoneNumber: raw.phoneNumber || raw.phone_number || raw.phone || '',
    nationality: raw.nationality || '',
    passportNumber: raw.passportNumber || raw.passport_number || '',
    passportExpiryDate: raw.passportExpiryDate || raw.passport_expiry_date || '',
    passportCountry: raw.passportCountry || raw.passport_country || '',
    knownTravelerNumber: raw.knownTravelerNumber || raw.known_traveler_number || '',
    frequentFlyerProgram: raw.frequentFlyerProgram || raw.frequent_flyer_program || '',
    frequentFlyerNumber: raw.frequentFlyerNumber || raw.frequent_flyer_number || '',
    seatPreference: raw.seatPreference || raw.seat_preference || '',
    specialRequests: raw.specialRequests || raw.special_requests || '',
  };
}

function normalizeFlightData(raw) {
  if (!raw) return {};
  if (typeof raw === 'string') {
    try { return JSON.parse(raw) || {}; } catch { return {}; }
  }
  return typeof raw === 'object' && !Array.isArray(raw) ? raw : {};
}

/**
 * Duffel hosts airline logos on a predictable CDN path keyed by IATA
 * code. Older bookings sometimes persisted flight_data without the
 * logo_symbol_url field, so we derive the URL from the carrier code as
 * a fallback — same asset the Trips page displays.
 */
export function airlineLogoUrlFromIata(iataCode) {
  const code = String(iataCode || '').trim().toUpperCase();
  if (!/^[A-Z0-9]{2,3}$/.test(code)) return '';
  return `https://assets.duffel.com/img/airlines/for-light-background/full-color-logo/${code}.svg`;
}

/** Same-origin PNG proxy — reliable on mobile/PWA (no canvas rasterization). */
export function airlineLogoProxyUrl(iataCode) {
  const code = String(iataCode || '').trim().toUpperCase();
  if (!/^[A-Z0-9]{2,3}$/.test(code)) return '';
  return `/api/airline-logo/${code}.png`;
}

/**
 * Some stored bookings keep the carrier prefix inside the flight-number
 * field ("UA 2318" instead of "2318"). Renderers prepend the carrier code
 * themselves, so left as-is that prints as "UAUA 2318". Strip a leading
 * carrier code (plus any separator) so the field is always the bare number.
 */
export function normalizeFlightNumber(raw, carrierCode) {
  const s = String(raw || '').trim();
  if (!s) return '';
  const code = String(carrierCode || '').trim().toUpperCase();
  if (code && s.toUpperCase().startsWith(code)) {
    const rest = s.slice(code.length).replace(/^[\s-]+/, '');
    if (rest) return rest;
  }
  return s;
}

/** Extract flight segments (across all slices) as a flat, sorted list of legs. */
function extractSegments(trip) {
  const legs = [];
  (trip || []).forEach((booking) => {
    const fd = normalizeFlightData(booking?.flightData || booking?.flight_data);
    const slices = Array.isArray(fd.slices) ? fd.slices : [];
    if (slices.length === 0) {
      // Uploaded / minimal-shape booking — synthesize a single leg from the
      // top-level departure/arrival fields FlightBooking already extracted.
      if (booking?.departure_city || booking?.arrival_city) {
        legs.push({
          sliceIndex: 0,
          segmentIndex: 0,
          origin: booking.departure_city || '',
          originCity: getCityName(booking.departure_city || ''),
          destination: booking.arrival_city || '',
          destinationCity: getCityName(booking.arrival_city || ''),
          departingAt: booking.departure_time || booking.departure_date || '',
          arrivingAt: booking.arrival_time || booking.arrival_date || '',
          departureDate: booking.departure_date || '',
          arrivalDate: booking.arrival_date || '',
          departureTime: (booking.departure_time || '').includes('T')
            ? (booking.departure_time || '').split('T')[1]?.slice(0, 5) || ''
            : booking.departure_time || '',
          arrivalTime: (booking.arrival_time || '').includes('T')
            ? (booking.arrival_time || '').split('T')[1]?.slice(0, 5) || ''
            : booking.arrival_time || '',
          marketingCarrier: booking.marketing_airline || '',
          operatingCarrier: '',
          marketingCarrierCode: '',
          flightNumber: '',
          aircraft: '',
          cabinClass: booking.cabinClass || booking.cabin_class || '',
        });
      }
      return;
    }
    slices.forEach((slice, sliceIdx) => {
      const segs = Array.isArray(slice?.segments) ? slice.segments : [];
      segs.forEach((seg, segIdx) => {
        const dep = seg?.departing_at || '';
        const arr = seg?.arriving_at || '';
        const mc = seg?.marketing_carrier || {};
        // Name-first carrier code: after an admin airline rename the stored
        // iata_code/logo still belong to the OLD carrier, and the statement
        // would print the new name over the old mark and code.
        const mcCode = resolveAirlineIata(mc.name) || mc.iata_code || '';
        legs.push({
          sliceIndex: sliceIdx,
          segmentIndex: segIdx,
          origin: seg?.origin?.iata_code || '',
          originCity: seg?.origin?.city_name || getCityName(seg?.origin?.iata_code || ''),
          originName: seg?.origin?.name || '',
          destination: seg?.destination?.iata_code || '',
          destinationCity: seg?.destination?.city_name || getCityName(seg?.destination?.iata_code || ''),
          destinationName: seg?.destination?.name || '',
          departingAt: dep,
          arrivingAt: arr,
          departureDate: dep.includes('T') ? dep.split('T')[0] : (dep.split(' ')[0] || ''),
          arrivalDate: arr.includes('T') ? arr.split('T')[0] : (arr.split(' ')[0] || ''),
          departureTime: dep.includes('T') ? dep.split('T')[1]?.slice(0, 5) || '' : (dep.split(' ')[1]?.slice(0, 5) || ''),
          arrivalTime: arr.includes('T') ? arr.split('T')[1]?.slice(0, 5) || '' : (arr.split(' ')[1]?.slice(0, 5) || ''),
          marketingCarrier: mc.name || '',
          marketingCarrierCode: mcCode,
          /** Marketing-carrier logo URLs (same name-first resolution the Trips
           *  page uses). The button component fetches these at click time and
           *  turns them into inline data URIs so the popup renders them even
           *  when the parent frame is offline / CORS-restricted. */
          marketingCarrierLogoUrl: resolveCarrierLogo(mc)
            || airlineLogoUrlFromIata(mcCode),
          operatingCarrier: seg?.operating_carrier?.name || '',
          flightNumber: normalizeFlightNumber(
            seg?.marketing_carrier_flight_number || seg?.operating_carrier_flight_number || '',
            mcCode,
          ),
          aircraft: seg?.aircraft?.name || '',
          cabinClass: seg?.passengers?.[0]?.cabin_class_marketing_name
            || seg?.passengers?.[0]?.cabin_class
            || booking.cabinClass
            || booking.cabin_class
            || fd.cabin_class
            || '',
          duration: seg?.duration || '',
        });
      });
    });
  });
  legs.sort((a, b) => {
    const da = new Date(a.departingAt || `${a.departureDate}T${a.departureTime || '00:00'}`).getTime();
    const db = new Date(b.departingAt || `${b.departureDate}T${b.departureTime || '00:00'}`).getTime();
    if (Number.isFinite(da) && Number.isFinite(db) && da !== db) return da - db;
    if (a.sliceIndex !== b.sliceIndex) return a.sliceIndex - b.sliceIndex;
    return a.segmentIndex - b.segmentIndex;
  });
  return legs;
}

/**
 * Extract pricing for a booking. Uses stored columns first (which for saved
 * trips match what the customer actually paid); if a column is missing it
 * back-derives from the others using the tiered service-fee rate — same
 * fallback used in the review flow.
 */
export function extractPricing(bookingRow) {
  /**
   * A Faye premium sits inside the stored totals (migration 156). Every
   * back-derivation below runs on the row without it, so the premium never
   * lands in "Base fare"; it comes back as its own line and in the total.
   */
  const insurance = insurancePremiumCents(bookingRow) / 100;
  const insurancePlan = readTripInsurance(bookingRow)?.label || null;
  const mainFlight = withoutInsurancePremium(bookingRow);
  const fd = normalizeFlightData(mainFlight?.flightData || mainFlight?.flight_data);
  const pm = fd._payment_method || fd.payment_method || mainFlight?.payment_method || 'installments';
  const currency = mainFlight?.currency || mainFlight?.total_currency || fd.total_currency || 'USD';

  const stored = {
    totalAmount: Number(mainFlight?.total_amount ?? mainFlight?.totalPrice ?? mainFlight?.totalAmount ?? 0) || 0,
    serviceFee: Number(mainFlight?.service_fee ?? mainFlight?.serviceFee ?? 0) || 0,
    holdFee: Number(mainFlight?.hold_fee ?? mainFlight?.holdFee ?? 0) || 0,
    grandTotal: Number(mainFlight?.grand_total ?? mainFlight?.grandTotal ?? 0) || 0,
    grandTotalWithHold: Number(
      mainFlight?.grand_total_with_hold
        ?? mainFlight?.grandTotalWithHold
        ?? mainFlight?.grand_total
        ?? mainFlight?.grandTotal
        ?? 0
    ) || 0,
  };

  const flightDataTotal = Number(fd.total_amount ?? 0) || 0;
  const flightDataBase = Number(fd.base_amount ?? 0) || 0;
  const baseFallback = flightDataBase || flightDataTotal || stored.totalAmount;

  const grandWithHold = stored.grandTotalWithHold || stored.grandTotal || (baseFallback + stored.serviceFee + stored.holdFee);
  let baseFare = 0;
  let serviceFee = stored.serviceFee;
  let holdFee = stored.holdFee;

  if (stored.totalAmount > 0 && grandWithHold > 0 && grandWithHold >= stored.totalAmount - 1) {
    baseFare = Math.max(0, grandWithHold - serviceFee - holdFee);
  } else if (grandWithHold > 0) {
    baseFare = Math.max(0, grandWithHold - serviceFee - holdFee);
  } else {
    baseFare = baseFallback;
  }
  if (baseFare === 0 && baseFallback > 0) baseFare = baseFallback;

  if (serviceFee === 0 && baseFare > 0 && grandWithHold > baseFare) {
    const rate = getServiceFeeRate(baseFare, pm === 'defera_capital');
    const naturalFee = baseFare * rate;
    const residual = Math.max(0, grandWithHold - baseFare);
    serviceFee = Math.min(naturalFee, residual);
    if (holdFee === 0) holdFee = Math.max(0, residual - serviceFee);
  }

  /**
   * Seats picked at checkout are financed inside the held subtotal, so every
   * back-derivation above (`grandWithHold − serviceFee − holdFee`) has already
   * swallowed them into `baseFare`. Break them back out into their own line so the
   * statement names the charge instead of inflating the fare — 13DYI1 printed a
   * $1,778.00 base fare for a $1,760.00 ticket plus an $18.00 seat.
   *
   * `checkoutSeatTotalFromFlightData` ignores post-booking `seat_purchase` buys on
   * purpose: those are billed on their own Stripe PI and are not in this total.
   */
  const seatTotal = checkoutSeatTotalFromFlightData(fd);
  const seatCount = checkoutSeatCountFromFlightData(fd);
  const baseFareExcludingSeats = Math.max(0, baseFare - seatTotal);

  // Unchanged in value — seats simply moved out of `baseFare` into their own term.
  const subtotalBeforeHold = baseFareExcludingSeats + seatTotal + serviceFee;
  const finalGrand = grandWithHold || (subtotalBeforeHold + holdFee);
  // The service fee was charged on fare + seats, so that's the honest denominator.
  const serviceFeeBase = baseFareExcludingSeats + seatTotal;

  return {
    currency,
    baseFare: baseFareExcludingSeats,
    seatTotal,
    seatCount,
    serviceFee,
    holdFee,
    subtotalBeforeHold,
    insurance,
    insurancePlan,
    // What the customer pays in all: the trip plus any Faye premium.
    grandTotalWithHold: Math.round((finalGrand + insurance) * 100) / 100,
    serviceFeePercent: serviceFeeBase > 0 ? Math.round((serviceFee / serviceFeeBase) * 100) : 0,
    holdFeePercent: subtotalBeforeHold > 0 ? Math.round((holdFee / subtotalBeforeHold) * 100) : 0,
    paymentMethod: pm,
    creditsTotal: Number(fd.credits_total || 0) || 0,
    milesUsed: Number(fd?.pricing_snapshot?.miles_used || 0) || 0,
  };
}

/**
 * Look up how the app labels each payment method for the customer. Kept in
 * sync with the naming used in Review / Modal ("Slice Pay", "Defera Capital",
 * "Defera Zero", "Reserve").
 */
export function paymentMethodLabel(method) {
  switch (method) {
    case 'defera_zero': return 'Defera Zero';
    case 'defera_capital': return 'Defera Capital';
    case 'reserve': return 'Reserve';
    case 'miles': return 'Defera Miles';
    case 'miles_installments': return 'Miles + Installments';
    case 'installments': return 'Slice Pay';
    default: return 'Installments';
  }
}

/** Human label for trip status, e.g. `pending` → "In Progress". */
export function tripStatusLabel(status) {
  const s = String(status || '').toLowerCase();
  switch (s) {
    case 'completed': return 'Completed';
    case 'confirmed': return 'Confirmed';
    case 'cancelled':
    case 'canceled': return 'Cancelled';
    case 'defaulted': return 'Defaulted';
    case 'refunded': return 'Refunded';
    case 'pending': return 'Paying Off';
    default: return status ? String(status).replace(/^./, (c) => c.toUpperCase()) : 'Pending';
  }
}

/**
 * Build a flat schedule + history list from the appropriate source.
 *
 * `scheduleData` is expected to be the raw payload from the Defera Zero /
 * Defera Capital `schedule` edge function when the trip is Stripe/NMI-backed.
 * If it's absent (Slice Pay trips or fetch failure), we fall back to the
 * booking's persisted `flight_data.payment_schedule`.
 *
 * Rows are ordered by date, with paid rows first (chronological) and upcoming
 * rows after (also chronological).
 */
export function buildPaymentTimeline({ scheduleData, mainFlight }) {
  const rows = [];

  if (scheduleData && typeof scheduleData === 'object') {
    const history = Array.isArray(scheduleData.payment_history) ? scheduleData.payment_history : [];
    const open = Array.isArray(scheduleData.open_invoices) ? scheduleData.open_invoices : [];
    const scheduleDates = scheduleData.schedule?.dates || [];

    history.forEach((p) => {
      const cents = Number(p?.amount_cents) || 0;
      if (cents <= 0) return;
      rows.push({
        date: p.date || p.paid_at || '',
        amountCents: cents,
        status: 'paid',
        label: p.type === 'one_time' ? 'Payment' : 'Installment',
      });
    });
    open.forEach((inv) => {
      const cents = Number(inv?.amount_cents) || 0;
      if (cents <= 0) return;
      rows.push({
        date: inv.date || inv.due_date || '',
        amountCents: cents,
        status: 'past_due',
        label: 'Past-due installment',
      });
    });
    scheduleDates.forEach((d) => {
      if (typeof d === 'object' && d?.date) {
        const cents = Number(d.amount_cents) || 0;
        if (d.paid_ahead) {
          rows.push({
            date: d.date,
            amountCents: 0,
            status: 'paid_ahead',
            label: 'Paid ahead',
          });
          return;
        }
        if (d.fee_skipped) {
          rows.push({
            date: d.date,
            amountCents: 0,
            status: 'skipped',
            label: 'Skipped',
          });
          return;
        }
        if (d.is_paid) return;
        if (cents <= 0) return;
        rows.push({
          date: d.date,
          amountCents: cents,
          status: 'upcoming',
          label: 'Installment',
        });
      } else if (typeof d === 'string') {
        rows.push({
          date: d,
          amountCents: Number(scheduleData?.stripe_unit_amount_cents)
            || Number(scheduleData?.schedule?.per_payment_cents)
            || 0,
          status: 'upcoming',
          label: 'Installment',
        });
      }
    });

    return sortAndDedupe(rows, scheduleData?.currency || 'USD');
  }

  // Fallback: use the persisted `payment_schedule` from the booking.
  const fd = normalizeFlightData(mainFlight?.flightData || mainFlight?.flight_data);
  const ps = fd.payment_schedule || {};
  if (ps && (ps.schedule?.weeklyPlan || ps.schedule?.biweeklyPlan || ps.schedule?.depositAmount != null)) {
    const activePlan = ps.payment_plan === 'biweekly' ? ps.schedule?.biweeklyPlan : ps.schedule?.weeklyPlan;
    if (ps.schedule?.depositAmount > 0) {
      const bookingDate = ps.booking_date || mainFlight?.created_at || mainFlight?.createdAt || '';
      rows.push({
        date: calendarDateOnly(bookingDate),
        amountCents: Math.round((Number(ps.schedule.depositAmount) || 0) * 100),
        status: 'paid',
        label: 'Deposit',
      });
    }
    (activePlan?.schedule || []).forEach((d) => {
      rows.push({
        date: calendarDateOnly(d),
        amountCents: Math.round((Number(activePlan?.paymentAmount) || 0) * 100),
        status: 'upcoming',
        label: 'Installment',
      });
    });
  }

  return sortAndDedupe(rows, mainFlight?.currency || 'USD');
}

function sortAndDedupe(rows, currency) {
  const paid = rows.filter((r) => r.status === 'paid' || r.status === 'paid_ahead');
  const pastDue = rows.filter((r) => r.status === 'past_due');
  const skipped = rows.filter((r) => r.status === 'skipped');
  const upcoming = rows.filter((r) => r.status === 'upcoming');

  const byDateAsc = (a, b) => (a.date || '').localeCompare(b.date || '');
  paid.sort(byDateAsc);
  pastDue.sort(byDateAsc);
  skipped.sort(byDateAsc);
  upcoming.sort(byDateAsc);

  return [...paid, ...pastDue, ...upcoming, ...skipped].map((r) => ({
    ...r,
    currency,
  }));
}

/**
 * Assemble the full document data structure. Everything downstream reads from
 * the fields on the returned object — the HTML renderer must not read the raw
 * trip / passenger / user objects itself.
 */
export function buildTripDocumentData({
  trip,
  passengers = [],
  currentUser,
  tripUser,
  scheduleData = null,
  route,
  documentIssueDate = new Date(),
}) {
  const bookings = Array.isArray(trip) ? trip : trip ? [trip] : [];
  const mainFlight = bookings[0] || {};
  const fd = normalizeFlightData(mainFlight?.flightData || mainFlight?.flight_data);

  const bookingReference = mainFlight?.booking_reference
    || mainFlight?.bookingReference
    || mainFlight?.trip_reference
    || mainFlight?.tripReference
    || fd.defera_booking_reference
    || '';

  const tripStatus = String(mainFlight?.trip_status || mainFlight?.status || 'pending').toLowerCase();

  const isCompleted = tripStatus === 'completed';
  const airlineConfirmation = isCompleted
    ? (mainFlight?.confirmation_number || mainFlight?.confirmationNumber || fd.confirmation_number || fd.airline_confirmation || '')
    : '';

  const segments = extractSegments(bookings);

  const originLeg = segments[0] || null;
  const destinationLeg = (() => {
    if (segments.length === 0) return null;
    const first = segments[0];
    for (let i = 1; i < segments.length; i += 1) {
      if (segments[i].sliceIndex !== first.sliceIndex) return segments[i - 1];
    }
    return segments[segments.length - 1];
  })();

  const totalSlices = bookings.reduce((n, b) => {
    const bfd = normalizeFlightData(b?.flightData || b?.flight_data);
    return n + (Array.isArray(bfd.slices) ? bfd.slices.length : 0);
  }, 0);
  const tripType = totalSlices > 1 ? 'Roundtrip' : 'One-way';

  const cabinClass = originLeg?.cabinClass
    || mainFlight?.cabinClass
    || mainFlight?.cabin_class
    || fd.cabin_class
    || '';

  const routeString = route
    || (originLeg && destinationLeg
      ? `${originLeg.originCity || originLeg.origin} → ${destinationLeg.destinationCity || destinationLeg.destination}`
      : '');

  const passengerList = (passengers || []).map(normalizePassenger);
  const passengerCount = passengerList.length
    || Number(mainFlight?.passenger_count || mainFlight?.passengerCount || 1);

  const pricing = extractPricing(mainFlight);

  const timeline = buildPaymentTimeline({ scheduleData, mainFlight });
  const paidRows = timeline.filter((r) => r.status === 'paid' || r.status === 'paid_ahead');
  const upcomingRows = timeline.filter((r) => r.status === 'upcoming');
  const pastDueRows = timeline.filter((r) => r.status === 'past_due');

  const totalPaidCents = paidRows.reduce((sum, r) => sum + (r.amountCents || 0), 0);
  const remainingCentsFromServer = Number(scheduleData?.remaining_cents);
  const totalCentsFromServer = Number(scheduleData?.total_amount_cents);
  const totalCents = Number.isFinite(totalCentsFromServer) && totalCentsFromServer > 0
    ? totalCentsFromServer
    : Math.round(pricing.grandTotalWithHold * 100);
  const remainingCents = Number.isFinite(remainingCentsFromServer) && remainingCentsFromServer >= 0
    ? remainingCentsFromServer
    : Math.max(0, totalCents - totalPaidCents);

  const bookingDate = fd?.payment_schedule?.booking_date
    || mainFlight?.created_at
    || mainFlight?.createdAt
    || '';

  const departureDate = originLeg?.departureDate
    || mainFlight?.departure_date
    || mainFlight?.departureDate
    || '';

  const customer = {
    name: buildCustomerName({ tripUser, currentUser }),
    email: tripUser?.email || currentUser?.email || '',
    phone: tripUser?.phone_number || tripUser?.phoneNumber || currentUser?.phone_number || '',
  };

  const documentNumber = buildDocumentNumber({ bookingReference, mainFlight });

  const paymentSchedule = fd.payment_schedule || null;
  const depositPercentage = paymentSchedule?.deposit_percentage ?? paymentSchedule?.depositPercentage ?? null;
  const paymentFrequency = paymentSchedule?.payment_plan || fd._payment_frequency || 'weekly';

  return {
    document: {
      title: 'Trip Statement',
      number: documentNumber,
      issueDate: documentIssueDate instanceof Date ? documentIssueDate : new Date(documentIssueDate),
      brand: {
        name: 'Defera',
        // Print-optimized logo (black on transparent), pre-cropped to the
        // bounding box of the wordmark. Generated once from the app-side
        // white-on-transparent asset — see scripts/build-print-logo.mjs.
        logoUrl: '/defera-print-logo.png',
        tagline: 'Layaway for travel.',
        // A printed document is offline by definition — the number is the
        // right thing here, unlike an in-app CTA. Sourced from the shared
        // constant so it can't drift into a fourth format.
        // Where to write to us: the Contact page's message form (no support
        // email address is published).
        contactPage: CONTACT_PAGE_DISPLAY,
        supportPhone: `+1 ${SUPPORT_SMS_DISPLAY}`,
        website: 'defera.app',
      },
    },
    trip: {
      reference: bookingReference,
      status: tripStatus,
      statusLabel: tripStatusLabel(tripStatus),
      type: tripType,
      route: routeString,
      routeShort: originLeg && destinationLeg
        ? `${originLeg.origin} → ${destinationLeg.destination}`
        : '',
      origin: originLeg?.origin || '',
      originCity: originLeg?.originCity || '',
      destination: destinationLeg?.destination || '',
      destinationCity: destinationLeg?.destinationCity || '',
      bookingDate,
      departureDate,
      cabinClass,
      passengerCount,
      airlineConfirmation,
      confirmationDisclosed: !!airlineConfirmation,
    },
    customer,
    passengers: passengerList,
    segments,
    pricing,
    payment: {
      method: pricing.paymentMethod,
      methodLabel: paymentMethodLabel(pricing.paymentMethod),
      frequency: paymentFrequency,
      depositPercentage,
      timeline,
      paidRows,
      upcomingRows,
      pastDueRows,
      totalCents,
      totalPaidCents,
      remainingCents,
      currency: pricing.currency || 'USD',
    },
  };
}

function buildCustomerName({ tripUser, currentUser }) {
  const src = tripUser || currentUser || {};
  const first = src.firstName || src.first_name || '';
  const last = src.lastName || src.last_name || '';
  const combined = `${first} ${last}`.trim();
  return combined || src.email || 'Customer';
}

function buildDocumentNumber({ bookingReference, mainFlight }) {
  const ref = (bookingReference || '').toString().toUpperCase();
  const created = mainFlight?.created_at || mainFlight?.createdAt || '';
  const shortDate = created ? String(created).slice(0, 10).replace(/-/g, '') : '';
  if (ref && shortDate) return `DFR-${ref}-${shortDate.slice(2)}`;
  if (ref) return `DFR-${ref}`;
  return 'DFR-STATEMENT';
}
